import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { formatApiError } from '@/lib/api';
import { restaurantService } from '@/services/restaurant.service';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo } from '@/components/Seo';
import { RestaurantLandingView } from '@/components/restaurant/RestaurantLandingView';
import { OwnerEventsSection } from '@/components/public/OwnerEventsSection';
import { CetsTrees } from '@/components/CetsTrees';
import { StorefrontBanner } from '@/components/StorefrontBanner';
import { RestaurantPublicReviews } from '@/components/restaurant/RestaurantPublicReviews';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { ArrowLeft, UtensilsCrossed, Loader2, CalendarPlus, CheckCircle2, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

const eur = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function RestaurantStructure() {
  const { ownerId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const locationHook = useLocation();
  const { isCustomer, addTable, openMiniCart } = useCart() || {};
  const [data, setData] = useState(undefined); // undefined=loading, null=notfound
  const [menuOpen, setMenuOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const [menuLoading, setMenuLoading] = useState(false);
  const [addingCart, setAddingCart] = useState(false);

  // reservation dialog
  const [bookOpen, setBookOpen] = useState(false);
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [slotsInfo, setSlotsInfo] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', people: '2', time: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await restaurantService.publicStructure(ownerId); setData(r); }
      catch (e) { setData(null); }
    })();
  }, [ownerId]);

  const openMenu = useCallback(async () => {
    setMenuOpen(true);
    if (menu) return;
    setMenuLoading(true);
    try { const r = await restaurantService.publicMenu(ownerId); setMenu(r?.menu || { categories: [] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setMenu({ categories: [] }); }
    finally { setMenuLoading(false); }
  }, [ownerId, menu]);

  const loadSlots = useCallback(async (d) => {
    setLoadingSlots(true); setSlots([]); setSlotsInfo('');
    try {
      const r = await restaurantService.publicAvailability(ownerId, { date: d });
      if (r?.active === false) { setSlotsInfo(r?.reason || 'Chiuso in questa data'); setSlots([]); }
      else {
        const s = (r?.slots || []).map((x) => (typeof x === 'string' ? x : x.time)).filter(Boolean);
        setSlots(s);
        if (!s.length) setSlotsInfo('Nessun orario disponibile per questa data');
      }
    } catch (e) { setSlotsInfo('Data non valida'); }
    finally { setLoadingSlots(false); }
  }, [ownerId]);

  const openBook = useCallback(() => {
    setBookOpen(true); setDone(null);
    if (user?.role === 'customer') {
      setForm((f) => ({ ...f, name: user.name || f.name, email: user.email || f.email, phone: user.phone || f.phone }));
    }
    loadSlots(date);
  }, [date, loadSlots, user]);
  useEffect(() => { if (bookOpen) { setForm((f) => ({ ...f, time: '' })); loadSlots(date); } }, [date]); // eslint-disable-line

  const submitBooking = async () => {
    if (!form.name.trim()) return toast.error('Inserisci il nome');
    if (!form.phone.trim()) return toast.error('Inserisci il telefono');
    if (!form.time) return toast.error('Scegli un orario');
    setSubmitting(true);
    try {
      const r = await restaurantService.publicReservation(ownerId, {
        name: form.name, phone: form.phone, email: form.email,
        people: Number(form.people), date, time: form.time, notes: form.notes,
      });
      setDone(r);
      toast.success('Richiesta inviata!');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const addTableToCart = async () => {
    if (!form.time) return toast.error('Scegli un orario');
    if (!isCustomer) {
      toast.info('Accedi per usare il carrello');
      navigate(`/cliente/accedi?next=${encodeURIComponent(locationHook.pathname + locationHook.search)}`);
      return;
    }
    setAddingCart(true);
    try {
      await addTable({ owner_id: ownerId, date, time: form.time, people: Number(form.people), notes: form.notes });
      toast.success('Tavolo aggiunto al carrello');
      setBookOpen(false);
      openMiniCart?.();
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Impossibile aggiungere al carrello');
    } finally { setAddingCart(false); }
  };

  return (
    <PublicLayout>
      <StorefrontBanner ownerId={ownerId} />
      <Seo title={data ? `${data.name} — Dove Mangiamo` : 'Ristorante'} description={data?.tagline || 'Vetrina del ristorante'} />
      <div className="sila-section py-6">
        <Link to="/dove-mangiare" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary" data-testid="back-to-list">
          <ArrowLeft className="h-4 w-4" />Tutti i ristoranti
        </Link>

        {data === undefined ? (
          <div className="space-y-4"><Skeleton className="h-[46vh] w-full rounded-3xl" /><Skeleton className="h-40 w-full rounded-2xl" /></div>
        ) : data === null ? (
          <div className="rounded-3xl border border-dashed border-border bg-muted/40 p-12 text-center" data-testid="restaurant-notfound">
            <UtensilsCrossed className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-3 font-medium text-foreground">Ristorante non disponibile</p>
            <p className="text-sm text-muted-foreground">La vetrina potrebbe non essere ancora pubblicata.</p>
            <Button asChild className="mt-4"><Link to="/dove-mangiare">Torna ai ristoranti</Link></Button>
          </div>
        ) : (
          <>
            <RestaurantLandingView data={data} publicMode onViewMenu={openMenu} onBook={openBook} />
            <div className="mt-5"><CetsTrees ownerId={ownerId} size="md" data-testid="cets-vetrina-badge" /></div>
          </>
        )}
      </div>

      {data && <RestaurantPublicReviews ownerId={ownerId} restaurantName={data.name} />}

      {data && <div className="sila-section"><OwnerEventsSection ownerId={ownerId} /></div>}

      {/* MENU COMPLETO */}
      <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="menu-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UtensilsCrossed className="h-5 w-5 text-primary" />Menu {data?.name ? `· ${data.name}` : ''}</DialogTitle>
            <DialogDescription>Consultazione del menu. Per ordinare, scansiona il QR al tavolo.</DialogDescription>
          </DialogHeader>
          {menuLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : !menu || (menu.categories || []).length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Menu non disponibile.</p>
          ) : (
            <div className="space-y-6">
              {menu.categories.map((c) => (
                <div key={c.id} data-testid={`menu-cat-${c.id}`}>
                  <h3 className="font-display text-lg font-bold text-foreground">{c.name}</h3>
                  {c.description && <p className="text-xs text-muted-foreground">{c.description}</p>}
                  <div className="mt-2 divide-y divide-border">
                    {(c.items || []).map((it) => (
                      <div key={it.id} className="flex items-start justify-between gap-3 py-2.5">
                        <div>
                          <p className="text-sm font-medium text-foreground">{it.name}</p>
                          {it.description && <p className="text-xs text-muted-foreground">{it.description}</p>}
                        </div>
                        {it.price > 0 && <span className="shrink-0 text-sm font-semibold text-foreground">{eur(it.price)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PRENOTA UN TAVOLO */}
      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent data-testid="book-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-primary" />Prenota un tavolo</DialogTitle>
            <DialogDescription>{data?.name ? `Richiesta di prenotazione presso ${data.name}.` : 'Invia una richiesta di prenotazione.'}</DialogDescription>
          </DialogHeader>

          {done ? (
            <div className="py-6 text-center" data-testid="book-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
              <p className="mt-3 font-semibold text-foreground">Richiesta inviata!</p>
              <p className="mt-1 text-sm text-muted-foreground">{done.message}</p>
              {done.code && <p className="mt-2 text-xs text-muted-foreground">Codice: <b className="text-foreground">{done.code}</b></p>}
              <Button className="mt-4" onClick={() => setBookOpen(false)}>Chiudi</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Data</Label><Input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="book-date" /></div>
                <div>
                  <Label>Persone</Label>
                  <Select value={form.people} onValueChange={(v) => setForm({ ...form, people: v })}>
                    <SelectTrigger data-testid="book-people"><SelectValue /></SelectTrigger>
                    <SelectContent>{Array.from({ length: 12 }, (_, i) => i + 1).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Orario</Label>
                {loadingSlots ? (
                  <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Carico disponibilità…</div>
                ) : slots.length ? (
                  <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })}>
                    <SelectTrigger data-testid="book-time"><SelectValue placeholder="Scegli un orario" /></SelectTrigger>
                    <SelectContent>{slots.map((s) => <SelectItem key={s} value={s} data-testid={`book-slot-${s}`}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <p className="rounded-lg border border-dashed border-border p-2.5 text-center text-xs text-muted-foreground" data-testid="book-noslots">{slotsInfo || 'Nessun orario disponibile'}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nome e cognome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mario Rossi" data-testid="book-name" /></div>
                <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 …" data-testid="book-phone" /></div>
              </div>
              <div><Label>Email (facoltativa)</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@…" data-testid="book-email" /></div>
              <div><Label>Note (facoltative)</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Allergie, seggiolone, occasione speciale…" data-testid="book-notes" /></div>
              <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
                <Button variant="ghost" onClick={() => setBookOpen(false)}>Annulla</Button>
                <Button variant="outline" onClick={addTableToCart} disabled={addingCart || submitting} className="gap-1.5" data-testid="book-addcart">
                  {addingCart ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}Aggiungi al carrello
                </Button>
                <Button onClick={submitBooking} disabled={submitting || addingCart} data-testid="book-submit">
                  {submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Invia richiesta
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
}

